import { useMemo, useState } from 'react';

import { useWindowSize } from '@shared/hooks/useWindowSize';

/**
 * hook to manage the card overlay behavior based on hover state and screen size.
 *
 * @param {boolean} grouped - Indicates if the cards are grouped.
 * @returns {Object} - An object containing handlers and state for card overlay behavior.
 * @returns {Function} handleMouseEnter - Handler to set hover state to true.
 * @returns {Function} handleMouseLeave - Handler to set hover state to false.
 * @returns {boolean} showOverlay - Indicates if the overlay should be shown.
 * @returns {boolean} scaleOnHover - Indicates if the carousel should enable the scale on hover feature.
 */
export const useCardOverlay = (grouped: boolean) => {
  const { isMobile } = useWindowSize();
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  const scaleOnHover = useMemo(() => !isMobile && !grouped, [isMobile, grouped]);

  const showOverlay = useMemo(() => isHovered && scaleOnHover, [isHovered, scaleOnHover]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    showOverlay,
    scaleOnHover,
  };
};
